/**
 * Ryan O'Dowd
 * 2022-03-22
 * © Copyright 2022 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import React, {
  useMemo,
  useState,
} from 'react';
import {
  TouchableOpacity,
  View,
} from 'react-native';
import GameTemplate from '@containers/Practice/GameTemplate';
import Reference from '@containers/Practice/Reference';
import Text from '@components/common/Text';
import getStyles from './styles';
import hooks from '@hooks';
import utilities from '@utils/utilities';

const FillInTheBlanks = (props: FillInTheBlanksProps) => {
  const styles = hooks.useStyles(getStyles);

  const [_currentGuesses, setCurrentGuesses] = useState([]);
  const [_questionComplete, setQuestionComplete] = useState(false); // @TODO: abstract this?
  const [_isCorrect, setIsCorrect] = useState(false);
  const [_isIncorrect, setIsIncorrect] = useState(false);
  const [_showCorrectAnswer, setShowCorrectAnswer] = useState(false);

  const verseNum = useMemo(() => {
    return props.passage.start_verse_number + Math.floor(Math.random() * (props.passage.end_verse_number - props.passage.start_verse_number + 1));
  }, [props.passage]);

  const passageVerses = hooks.useGetVersesForPassages([props.passage])[props.passage.id];
  const lessonPassageAndOptions = useMemo(() => {
    const verse = utilities.removeVerseFormatting(passageVerses[verseNum - props.passage.start_verse_number].verseText);
    const words = verse.split(' ');

    // @TODO: these should be settings or global variables
    let blanksRatio = 0.2;
    if (props.difficulty === 'medium') {
      blanksRatio = 0.5;
    } else if (props.difficulty === 'hard') {
      blanksRatio = 0.75;
    }

    const blankOptions = [];
    const numBlanks = Math.max(Math.floor(blanksRatio * words.length), 1);
    const indexesToBlank = [];
    for (let i = 0; i < numBlanks; i++) {
      let newIndex;
      do {
        newIndex = Math.floor(Math.random() * words.length);
      } while (indexesToBlank.includes(newIndex));
      indexesToBlank.push(newIndex);
    }
    indexesToBlank.forEach((indexToBlank) => {
      const wordToBlank = words[indexToBlank];
      if (wordToBlank) {
        // it's possible this is another blank because of double spaces after a period
        blankOptions.push({value: wordToBlank.replace(/[^a-zA-Z0-9-]/g, ''), index: indexToBlank});
      }
      if (props.difficulty === 'easy') {
        words[indexToBlank] = words[indexToBlank].replace(/[a-zA-Z0-9]/g, '_');
      } else {
        // make sure this isn't `' '`
        words[indexToBlank] = words[indexToBlank].replace(' ', '') ? '_____' : '';
      }
    });

    setCurrentGuesses(Array(blankOptions.length));

    return [words, blankOptions, verse];
  }, [props.passage, props.difficulty, verseNum, passageVerses]);

  const _isWrongGuess = (guess, guessIndex) => {
    // compare string values, not indexes (watch out for options that are the same word, even though the indexes are swapped)
    // @TODO: it shouldn't be possible for `guess` to be `null`; a value in `_currentGuesses` can be `null` temporarily due to unselecting an option from a blank, but the "check" button should be disabled when that's the case; seems to be a problem on `OnePlus 8T` and `B140DL` devices at the moment?
    return guess?.value.toLocaleLowerCase() !== lessonPassageAndOptions[1].sort((a, b) => a.index - b.index)[guessIndex].value.toLocaleLowerCase();
  };

  const _highlightWrongWords = (verse) => {
    const words = verse.split(' ').map((w) => `${w} `);
    _currentGuesses.forEach((g, i) => {
      if (_isWrongGuess(g, i)) {
        if (g) {
          // @TODO: it shouldn't be possible for `g` to be `null`; a value in `_currentGuesses` can be `null` temporarily due to unselecting an option from a blank, but the "check" button should be disabled when that's the case; seems to be a problem on `OnePlus 8T` and `B140DL` devices at the moment?
          words[g.index] = <Text key={i} style={[styles.correctAnswerText, styles.correctedWrongGuess]}>{words[g.index]}</Text>;
        }
      }
    });

    return words;
  };

  const _getRenderedText = () => {
    const [words, blankOptions] = lessonPassageAndOptions;
    const sortedBlankIndexes = blankOptions.map((o) => o.index).sort((a, b) => a - b);
    const transformedWords = words.map((word) => `${word} `);
    _currentGuesses.forEach((guess, index) => { // @TODO: the punctuation attached to text is disappearing when option is selected
      if (guess) {
        transformedWords[sortedBlankIndexes[index]] = (
          <TouchableOpacity
            key={guess.index}
            style={styles.guessTextWrapper}
            onPress={() => {
              setCurrentGuesses((currGuesses) => {
                return currGuesses.map((g) => {
                  if (!g || guess.index === g.index) {
                    return null;
                  } else {
                    return g;
                  }
                });
              });
            }}
          >
            <Text style={[styles.guessText, _questionComplete && _isWrongGuess(guess, index) && styles.wrongGuessText]}>{guess.value} </Text>
          </TouchableOpacity>
        );
      }
    });

    return transformedWords;
  };

  return (
    <GameTemplate
      subHeaderHeight={props.subHeaderHeight}
      questionComplete={_questionComplete}
      isIncorrect={_isIncorrect}
      isCorrect={_isCorrect}
      isCheckAnswerButtonDisabled={lessonPassageAndOptions[1].length !== _currentGuesses.filter((g) => g).length}
      onPass={() => props.onPass(_isCorrect, 1)}
      onCheckAnswer={() => {
        props.onCheckAnswer();
        // @TODO: if failed, add to practice
        // @TODO: add score
        setQuestionComplete(true);
        // @TODO: set state for correct or not and display value to user
        if (lessonPassageAndOptions[1].length === _currentGuesses.filter((g) => g).length && _currentGuesses.every((g, i) => {
          return !_isWrongGuess(g, i);
        })) {
          // @TODO: play sound
          setIsCorrect(true);
        } else {
          // @TODO: play sound
          setIsIncorrect(true);
        }
        setShowCorrectAnswer(true);
      }}
    >
      <Reference passage={props.passage.cloneWithChanges({startVerseNumber: verseNum, endVerseNumber: verseNum})} />
      <Text style={styles.lessonText}>{_getRenderedText()}</Text>
      <View style={styles.blankOptions}>
        {lessonPassageAndOptions[1].map((option) => {
          const isSelected = _currentGuesses.some((g) => g?.index === option.index);
          return (
            <TouchableOpacity
              key={option.index}
              style={[styles.option, isSelected && styles.hidden]}
              disabled={isSelected}
              onPress={() => {
                setCurrentGuesses((currGuesses) => {
                  const newGuesses = [...currGuesses];
                  let newGuessIndex = -1;
                  newGuesses.forEach((guess, index) => {
                    if (!guess && newGuessIndex < 0) {
                      newGuessIndex = index;
                    }
                  });
                  newGuesses[newGuessIndex] = option;

                  return newGuesses;
                });
              }}
            >
              <Text style={styles.optionText}>{option.value}</Text>
            </TouchableOpacity>
          );
        })}
      </View>
      {_showCorrectAnswer &&
        <View style={styles.correctAnswer}>
          {/* @TODO: :tada: could be replaced by graphics/gifs (for all test types) */}
          {_isIncorrect &&
            <Text style={styles.correctAnswerText}>
              {_highlightWrongWords(lessonPassageAndOptions[2])}
            </Text>
          }
        </View>
      }
    </GameTemplate>
  );
};

class FillInTheBlanksProps {
  difficulty: 'easy' | 'medium' | 'hard';
  onCheckAnswer: () => void;
  passage: any;
  onPass: (isCorrect: boolean, versesReviewed: number) => void;
  subHeaderHeight: number;
};

export default FillInTheBlanks;
